import React, { useEffect, useState } from 'react';
import { fetchDeliveryStatus } from '../../services/mockLogisticAPI';
import { Truck, CheckCircle2, Clock, MapPin, Package } from 'lucide-react';
import Map, { Marker, NavigationControl, Source, Layer } from 'react-map-gl/maplibre';
import 'maplibre-gl/dist/maplibre-gl.css';

const DeliveryTrackingWidget = ({ filters }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const res = await fetchDeliveryStatus(filters);
        setData(res);
      } catch (error) {
        console.error("Failed to fetch delivery status", error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [filters]);

  if (loading || !data) {
    return (
      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: '100%' }}>
        <div style={{ height: '24px', width: '200px', background: '#e2e8f0', borderRadius: '4px', marginBottom: '24px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
        <div style={{ height: '200px', width: '100%', background: '#f1f5f9', borderRadius: '8px', marginBottom: '16px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ height: '16px', width: '100%', background: '#f1f5f9', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
          <div style={{ height: '16px', width: '75%', background: '#f1f5f9', borderRadius: '4px', animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite' }}></div>
        </div>
      </div>
    );
  }

  // mapCN/MapLibre Style using OpenStreetMap raster tiles
  const mapStyle = {
    version: 8,
    sources: {
      osm: {
        type: 'raster',
        tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256,
        attribution: '&copy; OpenStreetMap Contributors'
      }
    },
    layers: [
      {
        id: 'osm',
        type: 'raster',
        source: 'osm'
      }
    ]
  };

  // Generate GeoJSON for polylines (routes)
  const routeFeatures = data.routes.map((route, i) => ({
    type: 'Feature',
    properties: {
      id: route.id,
      status: route.status,
      color: route.status === 'Delivered' ? '#16a34a' : route.status === 'On Transit' ? '#ea580c' : '#475569'
    },
    geometry: {
      type: 'LineString',
      // MapLibre uses [lng, lat], but Leaflet used [lat, lng]. We must reverse.
      coordinates: [
        [route.from[1], route.from[0]], 
        [route.to[1], route.to[0]]
      ]
    }
  }));

  const geojsonSource = {
    type: 'FeatureCollection',
    features: routeFeatures
  };

  return (
    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Truck size={20} color="#10b981" />
          <h2 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>Tracking Surat Jalan (mapCN)</h2>
        </div>
        <div style={{ display: 'flex', gap: '16px', fontSize: '13px', fontWeight: '600' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#10b981' }}></span>
            <span style={{ color: '#475569' }}>{data.summary.delivered} Delivered</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f59e0b' }}></span>
            <span style={{ color: '#475569' }}>{data.summary.onTransit} Transit</span>
          </div>
        </div>
      </div>

      <div style={{ flex: 1, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
        {/* Map Section */}
        <div style={{ borderRadius: '8px', overflow: 'hidden', border: '1px solid #e2e8f0', height: '400px', position: 'relative', zIndex: 0 }}>
          <Map
            initialViewState={{
              longitude: 106.8456,
              latitude: -6.2088,
              zoom: 5
            }}
            style={{ width: '100%', height: '100%' }}
            mapStyle={mapStyle}
          >
            <NavigationControl position="top-right" />
            
            {/* Draw Routes */}
            <Source id="routes" type="geojson" data={geojsonSource}>
              <Layer
                id="route-line-casing"
                type="line"
                paint={{
                  'line-color': '#ffffff',
                  'line-width': 8,
                  'line-opacity': 0.9
                }}
              />
              <Layer
                id="route-line"
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': 4,
                  'line-opacity': 1
                }}
              />
            </Source>

            {/* Custom Markers representing the mapCN / Shadcn aesthetic */}
            {data.routes.map((route, idx) => (
              <React.Fragment key={idx}>
                {/* Origin Marker */}
                <Marker longitude={route.from[1]} latitude={route.from[0]} anchor="bottom">
                  <div style={{ background: '#3b82f6', color: '#fff', padding: '6px', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
                    <Package size={14} />
                  </div>
                </Marker>
                
                {/* Destination Marker */}
                <Marker longitude={route.to[1]} latitude={route.to[0]} anchor="bottom">
                  <div style={{ background: route.status === 'Delivered' ? '#10b981' : route.status === 'On Transit' ? '#f59e0b' : '#94a3b8', color: '#fff', padding: '6px', borderRadius: '50%', border: '2px solid #fff', boxShadow: '0 2px 4px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Truck size={14} />
                  </div>
                </Marker>
              </React.Fragment>
            ))}
          </Map>
        </div>

        {/* Timeline Section */}
        <div style={{ display: 'flex', flexDirection: 'column', background: '#f8fafc', padding: '16px', borderRadius: '8px', border: '1px solid #e2e8f0', overflowY: 'auto', maxHeight: '400px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#334155', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Clock size={16} /> Live Updates
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {data.timeline.map((item, index) => (
              <div key={item.id} style={{ position: 'relative', paddingLeft: '24px', paddingBottom: '16px', borderLeft: index === data.timeline.length - 1 ? 'none' : '2px solid #e2e8f0' }}>
                <div style={{ position: 'absolute', left: '-9px', top: '0', background: '#fff', borderRadius: '50%' }}>
                  {item.type === 'success' && <CheckCircle2 size={16} color="#10b981" />}
                  {item.type === 'transit' && <Truck size={16} color="#f59e0b" />}
                  {item.type === 'pending' && <MapPin size={16} color="#94a3b8" />}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', marginTop: '-4px' }}>
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#1e293b' }}>{item.title}</span>
                  <span style={{ fontSize: '11px', color: '#64748b', marginTop: '2px' }}>{item.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeliveryTrackingWidget;
